import { State, TeamDetailsState } from '../configure-store';
import {
  getImageUrlWithDimensions,
  TABLE_HEADER_SIZE
} from '../../services/image-helper';
import { getAccountId, getIsSite } from './config';
import { AccountPermissionType, PermissionKey, SitePermissionType } from '../../services/server-types';
import { hasPermission } from '../../services/permissions-helper';
import { getNumberOfContributors } from './teamMembers';
import { isOwner } from './roleAreas';

export const isUserOwner = (state: State): boolean => {
  const { teamMembers: { users = [], teams = [] }, teamDetails: { userId } } = state;
  const teamMembersMerged = [...users, ...teams];
  const teamMember = teamMembersMerged.find(_teamMember => _teamMember.id === userId);
  return teamMember?.roles?.some(role => isOwner(state, role));
};

export const getTeamDetails = (state: State): TeamDetailsState => state.teamDetails;

export const getTeamName = (state: State): string => state.teamDetails.name;

export const getTeamImageUrl = (state: State): string => state.teamDetails.imageUrl;

const getTeamImageFullUrl = (state: State) => {
  const imageUrl = getTeamImageUrl(state);
  return getImageUrlWithDimensions(imageUrl, TABLE_HEADER_SIZE);
};

export const getTeamImageFullUrlForTableHeader = (state: State) => {
  return getTeamImageFullUrl(state);
};

export const getPendingOwnerEmail = (state: State) => {
  const member = [...state.teamMembers.invites, ...state.teamMembers.users].find(m => m.isPendingOwner);
  return member ? member.email : undefined;
};

export const getIsTeamOwnerAndSiteOwner = (state: State) =>
  (isUserOwner(state) && getAccountId(state) === getUserId(state));

export const getUserId = (state: State) => state.teamDetails.userId;

export const getInvitePeoplePermission = (state: State): boolean => (getPermission(state, PermissionKey.SEND_INVITE) || isUserOwner(state));

export const getManageRolesPermission = (state: State): boolean => (getPermission(state, PermissionKey.MANAGE_ROLES) || isUserOwner(state));

export const getPermission = (state: State, permissionKey: PermissionKey): boolean => {
  const permission = getIsSite(state) ? SitePermissionType[permissionKey] : AccountPermissionType[permissionKey];
  return hasPermission(permission, state.teamDetails.permissions);
};

export const getTransferOwnershipLink = (state: State): string => state.teamDetails?.transferOwnershipLink;

export const getContributorLimit = (state: State): number => state.teamDetails?.contributorLimit;

export const getNumberOfAvailableContributors = (state: State, contributorLimit = getContributorLimit(state)): number => contributorLimit - getNumberOfContributors(state);

export const getShouldEnforceContributors = (state: State): boolean => state.teamDetails?.shouldEnforceContributors;

export const getIsEnforceContributorsEnabled = (state: State): boolean => getIsSite(state) && getShouldEnforceContributors(state) && getPermission(state, PermissionKey.SEND_INVITE);

export const getShouldUpgradeBeforeInviteContributors = (state: State, contributorLimit?: number): boolean => {
  return getIsEnforceContributorsEnabled(state) && getNumberOfAvailableContributors(state, contributorLimit) < 1;
};

export const getShouldShowUpgradeToast = (state: State): boolean => getIsEnforceContributorsEnabled(state) && getNumberOfAvailableContributors(state) === 1;

export const getIsEnforceContributorsFreePlan = (state: State): boolean => getContributorLimit(state) < 3;

export const getLoadingTeammates = (state: State): boolean => state.teamDetails?.loadingTeammates;
